import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import "./projectAdmin.css";
import { useImmer } from "use-immer";
import CardEditor from "../../CardEditor";
import { UsernameDisplay } from "../UserPage";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Drawer,
  List,
  ListItemButton,
  Typography,
} from "@mui/material";
import { getComponentData, saveComponentTree } from "../../ComponentEditor/HelpersProject";
import { OptionsAdmin } from "./optionsAdmin";
import { FeedbackQuestionsAdmin } from "./feedbackQuestionAdmin";
import { AssetsManager } from "./AssetsManager";
import {
  ArrowUpward,
  Delete,
  BorderRight,
  Dashboard,
  Dataset,
  Description,
  Feedback,
  Image as ImageIcon,
  PriorityHigh,
  ExpandMore,
  Abc,
  Undo,
} from "@mui/icons-material";
import ProjectFontsAdmin from "./ProjectFontsAdmin";
import DeckPdfExportForm from "src/components/pages/projectAdmin/DeckPdfExport";

function DeckForm(props) {
  const [sheetStatus, setSheetStatus] = useState();
  const [psdStatus, setPsdStatus] = useState();
  const [confirmDeleteVisible, setConfirmDeleteVisible] = useState(false);
  const params = useParams();

  function updateSheet(evt) {
    evt.preventDefault();
    let formData = new FormData(evt.target);
    console.log("data in parent", formData);
    setSheetStatus("Loading sheet...");

    fetch("/api/update-deck-source-sheet/" + props.projectSlug, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        sheetId: evt.target.sheetId.value,
        rulesSheetName: evt.target.rulesSheetName.value,
        rulesRevSheetName: evt.target.rulesRevSheetName.value,
        dataSheetName: evt.target.dataSheetName.value,
        dataPreviewSheetName: evt.target.dataPreviewSheetName.value,
        deckId: evt.target.deckId.value,
        deckName: evt.target.deckName.value,
        projectSlug: props.projectSlug,
      }),
    })
      .then((response) => {
        if (response.ok) {
          return response.json();
        }
        return Promise.reject(response);
      })
      .then((data) => {
        console.log(data);
        props.updateDeckData(props.deckName, data);
        setSheetStatus("Update success");
      })
      .catch((error) => {
        console.log(error);
        if (!error.text) {
          setSheetStatus(error.message);
        } else {
          error.text().then((result) => {
            console.log(result);
            setSheetStatus(result);
          });
        }
      });
  }

  function updatePsd(evt) {
    evt.preventDefault();
    setPsdStatus("Loading .psd...");
    let formData = new FormData(evt.target);
    formData.set("clientFileName", formData.get("psdfile").name);
    fetch("/api/update-deck-source-psd/" + props.projectSlug, {
      method: "POST",
      body: formData,
    })
      .then(async (response) => {
        if (response.ok) {
          let newDeckData = await getDeckInfo(props.deckData.id);
          props.updateDeckData(props.deckName, newDeckData.deckdata);
          setPsdStatus(".psd updated");
        } else {
          const errorText = await response.text(); // Extract the error text
          console.log("Error response:", errorText);
          setPsdStatus("Upload error: " + errorText);
        }
      })
      .catch((error) => {
        console.error("Fetch error:", error); // Handle network or unexpected errors
        setPsdStatus("Network error: " + error.message);
      });
  }

  async function getDeckInfo(deckId) {
    return fetch("/api/deck/" + deckId)
      .then((response) => {
        if (response.ok) {
          return response.json();
        }
        throw new Error("Failed to fetch deck info");
      })
      .then((data) => {
        console.log("Deck info:", data);
        return data;
      })
      .catch((error) => {
        console.error("Error fetching deck info:", error);
      });
  }

  async function doDeleteDeck(projectSlug, deckName) {
    fetch("/api/projects/" + projectSlug + "/delete-deck/" + deckName, {
      method: "POST",
    })
      .then((response) => {
        if (response.ok) {
          props.onDeckDeleted(props.deckName);
        }
      })
      .catch((error) => {
        console.error("Error deleting deck:", error);
      });
  }

  let psdUpdateDate = null;
  if (props.deckData.layersLastUpdated) {
    psdUpdateDate = new Date(props.deckData.layersLastUpdated).toLocaleString("cs-CZ");
  }
  let psdFilename = props.deckData.clientPsdFilename ?? null;

  return (
    <div className="deck-admin-container">
      <Accordion
        className="nav-scroll-margin"
        id={"deck-" + props.deckName}
        elevation={3}
        expanded={props.isExpanded}
        onChange={() => {
          props.onManualToggle(props.deckName);
        }}
      >
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="h6">
            Deck "{props.deckName}" (ID {props.deckData.id})
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <div className="deck-admin-section-heading" style={{ marginTop: 0 }}>
            PDF export (for best results, please, use this in Chrome)
          </div>
          <DeckPdfExportForm
            projectSlug={props.projectSlug}
            deckInfo={props.deckData}
            globalAssets={props.globalAssets}
            deckName={props.deckName}
            projectFonts={props.projectFonts}
            deckFonts={props.deckFonts}
            cardFonts={props.cardFonts}
          />
          <div className="deck-admin-section-heading">Source .psd</div>
          <div className="deck-admin-psd-updated">
            {psdUpdateDate ? "Last update: " + psdUpdateDate : "Not uploaded yet"}
            {psdFilename ? <div>({psdFilename})</div> : "(name unknown)"}
          </div>
          <form onSubmit={updatePsd}>
            <div className="deck-admin-psd-form">
              <label htmlFor="psdfile">Upload new .psd file: </label>
              <input type="file" name="psdfile" />
              <div>
                <label htmlFor="generateRescaledImages">
                  Generate lower quality images (optimize CGO loading times)
                </label>
                <input
                  type="checkbox"
                  id="generateRescaledImages"
                  name="generateRescaledImages"
                  defaultChecked={true}
                />
              </div>
              <div>
                <input type="submit" value="Update" />
                {psdStatus}
              </div>
            </div>
            <input type="hidden" name="deckId" value={props.deckData.id}></input>
            <input type="hidden" name="deckCloudId" value={props.deckData.id}></input>
            <input type="hidden" name="deckName" value={props.deckName}></input>
          </form>
          <div className="deck-admin-section-heading">Source sheet</div>
          <div>
            <a target="_blank" href={"https://docs.google.com/spreadsheets/d/" + props.deckData.sheetId}>
              Open sheet
            </a>
          </div>
          <form onSubmit={updateSheet}>
            <label htmlFor="sheetId">Source spreadsheet id: </label>
            <input type="text" name="sheetId" defaultValue={props.deckData.sheetId} style={{ width: "400px" }}></input>
            <br></br>
            <label htmlFor="rulesSheetName">Rules sheet name: </label>
            <input type="text" name="rulesSheetName" defaultValue={props.deckData.rulesSheetName ?? "rules"}></input>
            <br></br>
            <label htmlFor="rulesRevSheetName">Rules (rev) sheet name: </label>
            <input
              type="text"
              name="rulesRevSheetName"
              defaultValue={props.deckData.rulesRevSheetName ?? "rules_rev"}
            ></input>
            <br></br>
            <label htmlFor="dataPreviewSheetName">Data preview sheet name: </label>
            <input
              type="text"
              name="dataPreviewSheetName"
              defaultValue={props.deckData.dataPreviewSheetName ?? "data_preview"}
            ></input>
            <br></br>
            <label htmlFor="dataSheetName">Data sheet name: </label>
            <input type="text" name="dataSheetName" defaultValue={props.deckData.dataSheetName ?? "data"}></input>
            <br></br>
            <input type="submit" value="Update source sheet"></input>
            <input type="hidden" name="deckId" value={props.deckData.id}></input>
            <input type="hidden" name="deckName" value={props.deckName}></input>
          </form>
          <div>{sheetStatus}</div>
          <div className="deck-admin-section-heading">Preview</div>
          <CardEditor
            deckData={props.deckData}
            globalAssets={props.globalAssets}
            deckName={props.deckName}
            projectFonts={props.projectFonts}
            deckFontOverrides={props.deckFonts}
            cardFontOverrides={props.cardFonts}
          />
          <div className="deck-admin-section-heading">Deck-specific assets</div>
          These assets are relevant for this deck only.
          <AssetsManager
            assets={props.deckData.assets}
            targetId={props.deckName}
            assetDeleted={props.assetDeleted}
            updateDeckAssets={props.updateDeckAssets}
            projectSlug={params.projectSlug}
            deckName={props.deckName}
          ></AssetsManager>
          <div className="deck-admin-section-heading">Administration</div>
          {!confirmDeleteVisible && (
            <Button variant="contained" color="error" onClick={() => setConfirmDeleteVisible(true)}>
              <Delete /> Delete deck
            </Button>
          )}
          {confirmDeleteVisible && (
            <div className="deck-delete-confirmation">
              <span>
                This will delete the deck <b>{props.deckName}</b>. Are you sure?
              </span>
              <Button
                variant="contained"
                color="error"
                onClick={() => {
                  doDeleteDeck(props.projectSlug, props.deckName);
                  setConfirmDeleteVisible(false);
                }}
              >
                <Delete />
                Confirm Delete
              </Button>
              <Button variant="contained" onClick={() => setConfirmDeleteVisible(false)}>
                <Undo />
                Cancel
              </Button>
            </div>
          )}
        </AccordionDetails>
      </Accordion>
    </div>
  );
}

function ProjectAdmin(props) {
  let params = useParams();
  let projectSlug = params.projectSlug;
  let [projectInfo, setProjectInfo] = useImmer(null);
  let [updateSuccess, setUpdateSucces] = useState(null);
  let [sheetUpdateStatus, setSheetUpdateStatus] = useState(null);
  let [navigationOpen, setNavigationOpen] = useState(true);
  const [openDecks, setOpenDecks] = useState([]);
  const [highlightedSection, setHighlightedSection] = useState("project-admin-top");
  const [positionLoaded, setPositionLoaded] = useState(false);

  const canEdit = ["designer", "mod"].includes(getHighestRole()) || localStorage.getItem("user-role").includes("admin");
  const savePosition = canEdit && localStorage.getItem("project-admin-save-position") === "1";

  useEffect(() => {
    document.title = `Admin - ${projectSlug}`;
  }, [projectSlug]);

  const handleScroll = useCallback(() => {
    if (savePosition) {
      localStorage.setItem("project-admin-scroll-" + projectSlug, window.scrollY);
    }
    let elements = document.getElementsByClassName("nav-scroll-margin");
    if (elements.length === 0) {
      return;
    }
    let breakingPoint = window.innerHeight / 6;
    let closest = Array.from(elements)
      .filter((el) => el.getBoundingClientRect().top - breakingPoint < 0)
      .reduce((prevEl, currEl) => {
        if (
          Math.abs(prevEl.getBoundingClientRect().top - breakingPoint) <
          Math.abs(currEl.getBoundingClientRect().top - breakingPoint)
        ) {
          return prevEl;
        }
        return currEl;
      });
    setHighlightedSection(closest?.id);
  }, [projectSlug, savePosition]);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [handleScroll]);

  useEffect(() => {
    if (savePosition && projectInfo && !positionLoaded) {
      let savedOpenDecks = JSON.parse(localStorage.getItem("project-admin-open-decks-" + projectSlug) || "[]");
      let savedPosition = localStorage.getItem("project-admin-scroll-" + projectSlug);
      setOpenDecks(savedOpenDecks);
      setTimeout(() => {
        window.scrollTo(0, savedPosition || 0);
        setPositionLoaded(true);
      }, 500);
    }
  }, [projectSlug, projectInfo, positionLoaded, savePosition]);

  useEffect(() => {
    if (openDecks.length !== 0) {
      localStorage.setItem("project-admin-open-decks-" + projectSlug, JSON.stringify(openDecks));
    }
  }, [openDecks, projectSlug]);

  function updateDeckAssets(deckName, newData) {
    setProjectInfo((projectInfo) => {
      projectInfo.decks[deckName].assets = Object.assign(projectInfo.decks[deckName].assets, newData);
    });
  }

  function assetDeleted(deckName, assetName) {
    setProjectInfo((projectInfo) => {
      if (projectInfo.decks[deckName]) {
        delete projectInfo.decks[deckName].assets[assetName];
      } else {
        delete projectInfo.globalAssets[assetName];
      }
    });
  }

  function updateDeckData(deckName, newData) {
    setProjectInfo((projectInfo) => {
      projectInfo.decks[deckName] = Object.assign(projectInfo.decks[deckName], newData);
    });
  }

  function updateGlobalAssets(newData) {
    setProjectInfo((projectInfo) => {
      let newAssets = Object.assign(projectInfo.globalAssets, newData);
      projectInfo.globalAssets = newAssets;
    });
  }

  const getProjectInfo = useCallback(async () => {
    return fetch("/api/projects/get-project-detail/" + params.projectSlug + "?status=none", {
      method: "GET",
    })
      .then((response) => {
        console.log(response);
        if (response.ok) {
          return response.json();
        } else {
          setProjectInfo("notfound");
        }
        return Promise.reject(response);
      })
      .then((res) => {
        console.log("setting project info");
        console.log(res);
        setProjectInfo(res);
      });
  }, [params.projectSlug, setProjectInfo]);

  async function updateDataSheet(form) {
    setSheetUpdateStatus("Updating...");
    return fetch("/api/projects/update-data-sheet/" + params.projectSlug, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ dataSheetId: form.dataSheetId.value }),
    })
      .then((response) => {
        console.log(response);
        if (response.ok) {
          return response.text();
        }
        return Promise.reject(response);
      })
      .then((message) => {
        setSheetUpdateStatus(message);
      })
      .catch(async (message) => {
        setSheetUpdateStatus(message.statusText);
      });
  }

  async function uploadDataSheet(evt) {
    evt.preventDefault();
    setSheetUpdateStatus("Uploading...");
    let formData = new FormData(evt.target);
    fetch("/api/projects/upload-data-sheet/" + params.projectSlug, {
      method: "POST",
      body: formData,
    }).then((response) => {
      if (response.ok) {
        setSheetUpdateStatus("Upload success");
      }
    });
  }

  async function createDeck(form) {
    setSheetUpdateStatus("Creating deck...");
    return fetch("/api/projects/create-deck/" + params.projectSlug, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ deckName: form.deckName.value }),
    })
      .then((response) => {
        console.log(response);
        if (response.ok) {
          return response.text();
        }
        return Promise.reject(response);
      })
      .then((message) => {
        setProjectInfo((project) => {
          return project;
        });
      });
  }

  async function updateProjectInfo(form) {
    console.log(form);
    let name = form.name;
    let newValue = form[name].value;

    return fetch("/api/projects/update-project-info/" + params.projectSlug + "?name=" + name, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ newValue }),
    }).then((response) => {
      console.log(response);
      if (response.ok) {
        setUpdateSucces({ name, message: "Update success!" });
        return response.json();
      }
      return Promise.reject(response);
    });
  }

  async function addRole(username, inviteCode, roleName) {
    return fetch("/api/projects/add-role/" + params.projectSlug, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ username, roleName, inviteCode }),
    }).then((response) => {
      console.log(response);
      if (response.ok) {
        setUpdateSucces({ name: roleName, message: "Update success!" });
      }
      return Promise.reject(response);
    });
  }

  async function reloadProjectFonts() {
    fetch(`/api/projects/${params.projectSlug}/fonts`)
      .then((response) => {
        if (response.ok) {
          return response.json();
        }
      })
      .then((data) => {
        if (data && data.fonts) {
          setProjectInfo((project) => {
            project.projectFonts = data.fonts;
          });
        }
      });
  }

  const navigate = useNavigate();

  useEffect(() => {
    getProjectInfo();
  }, [getProjectInfo]);

  let projectDescription = null;
  if (projectInfo === "notfound") {
    return <div>Project not found</div>;
  }
  if (projectInfo?.description) {
    projectDescription = (
      <div id="project-admin-description" className="nav-scroll-margin">
        <form
          onSubmit={(evt) => {
            evt.preventDefault();
            updateProjectInfo(evt.target);
          }}
          name="description"
        >
          <h2>Project description</h2>
          <textarea cols="100" rows="10" name="description" defaultValue={projectInfo.description}></textarea>
          <br />
          <input type="submit" value="Update description"></input>
          {updateSuccess?.name == "description" ? <div>{updateSuccess.message}</div> : <></>}
        </form>
      </div>
    );
  }

  let projectVisibility = (
    <form name="visibility">
      <h2>Project visibility</h2>
      <select
        name="visibility"
        defaultValue={projectInfo?.visibility}
        onChange={(evt) => updateProjectInfo({ name: "visibility", visibility: { value: evt.target.value } })}
      >
        <option value="public">Public</option>
        <option value="closed">Closed</option>
        <option value="unlisted">Unlisted</option>
      </select>
      <br />
      {updateSuccess?.name == "visibility" ? <div>{updateSuccess.message}</div> : <></>}
    </form>
  );

  let roles = [<h2>Project roles</h2>];
  let roleDescription = {
    designer: "Designer can adjust implementation of the game, and add mods.",
    mod: "Mods can adjust the project description and visibility, and add players.",
    player: "Players can create and join tables and play the game.",
  };

  function getHighestRole() {
    if (!projectInfo?.roles) {
      return null;
    }
    for (let roleName of ["designer", "mod", "player"]) {
      if (projectInfo?.roles[roleName]?.includes(localStorage.getItem("user-name"))) {
        return roleName;
      }
    }
  }

  function canAddRole(role) {
    if (localStorage.getItem("user-role").includes("admin")) {
      return true;
    }
    let myRole = getHighestRole();
    if (myRole === "designer") {
      return role !== "designer";
    }
    if (myRole === "mod") {
      return role === "player";
    }
  }

  function onDeckDeleted(deckName) {
    setProjectInfo((project) => {
      if (project.decks && project.decks[deckName]) delete project.decks[deckName];
    });
  }

  for (let roleName of ["designer", "mod", "player"]) {
    roles.push(
      <section className="role-section">
        <h2>{roleName}</h2>
        <div>{roleDescription[roleName]}</div>
        {canAddRole(roleName) ? (
          <form
            name={"role-" + roleName}
            onSubmit={(evt) => {
              evt.preventDefault();
              addRole(evt.target[roleName + "Name"]?.value, evt.target[roleName + "InviteCode"]?.value, roleName);
            }}
          >
            <note>One of these fields is sufficient</note>
            <br></br>
            <label htmlFor={roleName + "Name"}>New {roleName} username</label>
            <input name={roleName + "Name"}></input>
            <br></br>
            <label htmlFor={roleName + "Invitecode"}>New {roleName} invitecode</label>
            <input name={roleName + "InviteCode"}></input>
            <br />
            <input type="submit" value={"Add " + roleName}></input>
            {updateSuccess?.name == roleName ? <div>{updateSuccess.message}</div> : <></>}
          </form>
        ) : null}
        <div>
          <h3>Current project {roleName}s</h3>
          <div className="username-list">
            {(projectInfo?.roles ?? {})[roleName]?.map((mod) => (
              <UsernameDisplay username={mod}></UsernameDisplay>
            ))}
          </div>
        </div>
      </section>,
    );
  }

  let sheetLink = null;
  if (projectInfo?.dataSheetId) {
    sheetLink = (
      <a target="_blank" href={"https://docs.google.com/spreadsheets/d/" + projectInfo?.dataSheetId}>
        Open data sheet in Google Sheets
      </a>
    );
  }

  let dataSheet = (
    <div id="project-admin-datasheet" className="nav-scroll-margin">
      <h2>Data sheet</h2>
      {sheetLink}
      <form
        onSubmit={(evt) => {
          evt.preventDefault();
          updateDataSheet(evt.target);
        }}
      >
        <label htmlFor="dataSheetId">Data sheet id:</label>
        <input type="text" name="dataSheetId" defaultValue={projectInfo?.dataSheetId}></input>
        <input type="submit" value="Update data sheet"></input>
      </form>
      <form
        onSubmit={(evt) => {
          evt.preventDefault();
          uploadDataSheet(evt);
        }}
      >
        <label htmlFor="xlsx-file">Upload datasheet as .xlsx</label>
        <input type="file" name="xlsx-file"></input>
        <input type="submit" value="Upload .xlsx"></input>
      </form>
      <div>{sheetUpdateStatus}</div>
    </div>
  );

  let deckForm = (
    <form
      id="new-deck-form"
      onSubmit={(evt) => {
        evt.preventDefault();
        createDeck(evt.target);
      }}
    >
      <h3>Create new deck</h3>
      <label htmlFor="deckName">Deck name</label>
      <input type="text" name="deckName"></input>
      <input type="submit" value="Create"></input>
    </form>
  );

  const handleManualDeckToggle = (deckName) => {
    setOpenDecks((prevOpenDecks) => {
      if (prevOpenDecks.includes(deckName)) {
        return prevOpenDecks.filter((name) => name !== deckName);
      } else {
        return [...prevOpenDecks, deckName];
      }
    });
  };

  let decks = null;
  if (projectInfo?.decks) {
    let deckList = [];
    for (let deckName of Object.keys(projectInfo.decks).toSorted()) {
      deckList.push(
        <DeckForm
          deckName={deckName}
          deckData={projectInfo.decks[deckName]}
          updateDeckAssets={updateDeckAssets}
          assetDeleted={assetDeleted}
          updateDeckData={updateDeckData}
          projectSlug={params.projectSlug}
          globalAssets={projectInfo.globalAssets}
          isExpanded={openDecks.includes(deckName)}
          onManualToggle={handleManualDeckToggle}
          projectFonts={projectInfo.projectFonts}
          deckFonts={projectInfo.deckCardFonts.filter((df) => df.deckName === deckName && df.cardName === null)}
          cardFonts={projectInfo.deckCardFonts.filter((df) => df.deckName === deckName && df.cardName !== null)}
          onDeckDeleted={onDeckDeleted}
        ></DeckForm>,
      );
    }
    decks = <div className="project-admin-decks-section">{deckList}</div>;
  }

  const handleNavigate = () => {
    navigate(`/projects/${projectSlug}/admin/component-editor`);
  };

  const handleUpdateDevFromTable = () => {
    fetch(`/api/projects/${projectSlug}/update-dev-from-latest-table`, {
      method: "POST",
    }).then((response) => {
      if (response.ok) {
        return;
      }
      return Promise.reject(response);
    });
  };

  const handlePushDevComponentsToProd = () => {
    getComponentData(projectSlug, true).then(
      (data) => {
        console.log("Data from components", JSON.stringify(data));
        saveComponentTree(projectSlug, false, data).then(
          () => {
            console.log("Data pushed to production");
          },
          (error) => {
            console.error("Error pushing data to production", error);
          },
        );
      },
      (error) => {
        console.error("Error getting data from developement components", error);
      },
    );
  };

  function navigateTo(sectionId) {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
      //setTimeout(() => { setHighlitedSection(sectionId) }, 1000);
    }
  }

  function navigateToDeck(deckName) {
    setOpenDecks((prevOpenDecks) => {
      if (!prevOpenDecks.includes(deckName)) {
        return [...prevOpenDecks, deckName];
      } else {
        return prevOpenDecks;
      }
    });
    setTimeout(() => {
      navigateTo("deck-" + deckName);
    }, 250);
  }

  let drawerSx = {
    width: "250px",
    flexShrink: 0,
    "& .MuiDrawer-paper": {
      width: "250px",
      boxSizing: "border-box",
      marginTop: "50px",
      borderRight: "2px solid black",
      height: "calc(100vh - 50px)",
    },
  };

  let highlightedNavSx = {
    backgroundColor: "#eeeeee",
    color: "#1976d2",
  };

  const navMenuItemDefs = [];
  navMenuItemDefs.push({ id: "project-admin-top", icon: <ArrowUpward />, label: "Top" });
  navMenuItemDefs.push({ id: "project-admin-description", icon: <Description />, label: "Description" });
  if (canEdit) {
    navMenuItemDefs.push({ id: "project-admin-feedback", icon: <Feedback />, label: "Feedback form" });
    navMenuItemDefs.push({ id: "project-admin-danger-zone", icon: <PriorityHigh />, label: "Game implementation" });
    navMenuItemDefs.push({ id: "project-admin-global-assets", icon: <ImageIcon />, label: "Global assets" });
    navMenuItemDefs.push({ id: "project-admin-project-fonts", icon: <Abc />, label: "Project fonts" });
    navMenuItemDefs.push({ id: "project-admin-datasheet", icon: <Dataset />, label: "Data sheet" });
    navMenuItemDefs.push({ id: "project-admin-decks", icon: <Dashboard />, label: "Decks" });
    for (let deckName of Object.keys(projectInfo?.decks ?? {}).toSorted()) {
      navMenuItemDefs.push({ id: deckName, icon: <BorderRight />, label: deckName, isDeck: true });
    }
  }
  const navListItems = [];
  for (let itemDef of navMenuItemDefs) {
    if (itemDef.isDeck) {
      navListItems.push(
        <ListItemButton
          style={{ marginLeft: "1rem" }}
          onClick={() => navigateToDeck(itemDef.id)}
          sx={highlightedSection === "deck-" + itemDef.id ? highlightedNavSx : {}}
        >
          {itemDef.icon}
          {itemDef.label}
        </ListItemButton>,
      );
    } else {
      navListItems.push(
        <ListItemButton
          onClick={() => navigateTo(itemDef.id)}
          sx={highlightedSection === itemDef.id ? highlightedNavSx : {}}
        >
          {itemDef.icon}
          {itemDef.label}
        </ListItemButton>,
      );
    }
  }

  if (!projectInfo) {
    return <div>Loading project info...</div>;
  }

  const cardCount = (sum, card) => {
    if (card.__count) {
      return sum + +card.__count;
    }
    if (card.count) {
      return sum + +card.count;
    }
    if (card.Count) {
      return sum + +card.Count;
    }
    return sum + 1;
  };

  return (
    <div>
      <Drawer
        variant="permanent"
        anchor="left"
        open={navigationOpen}
        onClose={() => setNavigationOpen(false)}
        sx={drawerSx}
      >
        <List>{navListItems}</List>
      </Drawer>
      <div className="project-admin-content nav-open">
        <span id="project-admin-top" className="project-admin-title nav-scroll-margin">
          {projectSlug} administration
        </span>
        {projectVisibility}
        {roles}
        {projectDescription}
        {canEdit ? (
          <>
            <FeedbackQuestionsAdmin questions={projectInfo.formQuestions} projectSlug={projectSlug} />
            <h2 id="project-admin-danger-zone" className="nav-scroll-margin">
              Danger zone ahead: following forms alter the game implementation
            </h2>
            <Button variant="contained" onClick={handleNavigate}>
              Edit game implementation
            </Button>
            <Button variant="contained" color="warning" onClick={handleUpdateDevFromTable}>
              Update DEV components with latest table data
            </Button>
            <Button variant="contained" color="warning" onClick={handlePushDevComponentsToProd}>
              Update PROD components with DEV data
            </Button>
            <br />
            <br />
            <OptionsAdmin options={projectInfo.options} projectSlug={projectSlug} />
            <h2 id="project-admin-global-assets" className="nav-scroll-margin">
              Global assets
            </h2>
            The following assets are used as icons or fonts in decks, as well as in log messages, resource flyers,
            buttons and status bar messages.
            <AssetsManager
              assets={projectInfo.globalAssets}
              assetDeleted={assetDeleted}
              projectSlug={params.projectSlug}
              updateGlobalAssets={updateGlobalAssets}
            />
            <h2 id="project-admin-project-fonts" className="nav-scroll-margin">
              Project fonts
            </h2>
            <ProjectFontsAdmin reloadProjectFonts={reloadProjectFonts} />
            {dataSheet}
            <h2 id="project-admin-decks" className="nav-scroll-margin">
              Decks
            </h2>
            <table className="decks-overview-table">
              <thead>
                <tr>
                  <th>Deck Name</th>
                  <th>Unique card count</th>
                  <th>Total card count</th>
                </tr>
              </thead>
              <tbody>
                {projectInfo.decks &&
                  Object.entries(projectInfo.decks)
                    .sort((a, b) => a[0].localeCompare(b[0]))
                    .map(([deckName, deckData]) => (
                      <tr key={deckName}>
                        <td>{deckName}</td>
                        {deckData?.cards ? (
                          <>
                            <td>{Object.values(deckData.cards).length}</td>
                            <td>{Object.values(deckData.cards).reduce(cardCount, 0)}</td>
                          </>
                        ) : (
                          <>
                            <td colSpan="2">No card data (No cards spreadsheet bound?)</td>
                          </>
                        )}
                      </tr>
                    ))}
              </tbody>
            </table>
            {deckForm}
            {decks}
          </>
        ) : null}
      </div>
    </div>
  );
}

export { ProjectAdmin };
